// Generisk min/max range-slider. Två <input type="range"> överlagrade.
// Håller min <= max automatiskt.
//
// Implementation: vi använder en wrapper med fast höjd och korrekt
// positionerade inputs (inte inset-0 trick) så tummarna inte sticker ut
// utanför slidern-ytan. Cross-browser CSS i <style> nollställer native
// track-styling så bara vårt visuella spår (det fyllda intervallet) syns.

import { useId } from 'react';

interface Props {
  label: string;
  min: number | null;
  max: number | null;
  bounds: { min: number; max: number; step?: number };
  format?: (n: number) => string;
  onChange: (next: { min: number | null; max: number | null }) => void;
}

export default function RangeSlider({ label, min, max, bounds, format, onChange }: Props) {
  const id = useId();
  const step = bounds.step ?? 1;
  const lo = min ?? bounds.min;
  const hi = max ?? bounds.max;
  const fmt = format ?? ((n: number) => String(n));

  const setLo = (v: number) => {
    const next = Math.min(v, hi);
    onChange({ min: next === bounds.min ? null : next, max });
  };
  const setHi = (v: number) => {
    const next = Math.max(v, lo);
    onChange({ min, max: next === bounds.max ? null : next });
  };

  // Procentposition för det fyllda spåret
  const span = bounds.max - bounds.min || 1;
  const leftPct = ((lo - bounds.min) / span) * 100;
  const rightPct = ((hi - bounds.min) / span) * 100;

  // Padding så tummarna (18px) inte klipps vid kanterna
  const THUMB_PX = 9; // halva thumb-bredden

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-sm">
        <label htmlFor={id} className="font-medium text-slate-700">
          {label}
        </label>
        <span className="text-xs tabular-nums text-slate-500">
          {fmt(lo)} – {fmt(hi)}
        </span>
      </div>

      {/* Wrapper med fast höjd och padding för tummar. Bredden = 100% av föräldern. */}
      <div
        className="relative"
        style={{ height: '24px', paddingLeft: THUMB_PX, paddingRight: THUMB_PX }}
      >
        {/* spår (grå) */}
        <div
          className="absolute rounded bg-slate-200"
          style={{ left: THUMB_PX, right: THUMB_PX, top: '50%', height: '4px', transform: 'translateY(-50%)' }}
        />
        {/* fyllt intervall (indigo) */}
        <div
          className="absolute rounded bg-indigo-500"
          style={{
            left: `calc(${THUMB_PX}px + ${leftPct}% * (100% - ${2 * THUMB_PX}px) / 100%)`,
            right: `calc(${THUMB_PX}px + ${100 - rightPct}% * (100% - ${2 * THUMB_PX}px) / 100%)`,
            top: '50%',
            height: '4px',
            transform: 'translateY(-50%)',
          }}
        />

        {/* Övre input (max-thumb) — placerad överst så vid klick på mitten vinner den */}
        <input
          id={id}
          type="range"
          min={bounds.min}
          max={bounds.max}
          step={step}
          value={hi}
          onChange={(e) => setHi(Number(e.target.value))}
          className="range-input range-input--hi absolute inset-0 w-full appearance-none bg-transparent"
        />
        {/* Nedre input (min-thumb) */}
        <input
          type="range"
          min={bounds.min}
          max={bounds.max}
          step={step}
          value={lo}
          onChange={(e) => setLo(Number(e.target.value))}
          className="range-input range-input--lo absolute inset-0 w-full appearance-none bg-transparent"
        />
      </div>

      <style>{`
        .range-input {
          pointer-events: none;
          height: 24px;
        }
        .range-input::-webkit-slider-runnable-track { background: transparent; height: 24px; }
        .range-input::-moz-range-track { background: transparent; height: 24px; }

        .range-input::-webkit-slider-thumb {
          pointer-events: auto;
          -webkit-appearance: none;
          appearance: none;
          width: 18px; height: 18px; border-radius: 9999px;
          background: white; border: 2px solid #4f46e5; cursor: grab;
          box-shadow: 0 1px 2px rgba(0,0,0,.1);
          margin-top: 3px; /* centrerar 18px-thumb på 24px-spåret */
        }
        .range-input::-moz-range-thumb {
          pointer-events: auto;
          width: 18px; height: 18px; border-radius: 9999px;
          background: white; border: 2px solid #4f46e5; cursor: grab;
        }

        /* Z-index: max-thumb överst så dess hover/klick fångas före min */
        .range-input--hi { z-index: 2; }
        .range-input--lo { z-index: 1; }
        /* När vi drar min-thumb åt höger mot max, ge den tillfälligt högre z */
        .range-input--lo:active { z-index: 3; }
      `}</style>
    </div>
  );
}
